import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export const downloadChatFile = async (path, filename) => {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(data), a = document.createElement("a");
    a.href = url; a.download = filename || "attachment"; a.click(); setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) { toast.error("This attachment is unavailable or you no longer have access."); }
};

export const ChatImage = ({ threadId, fileId, alt, className = "h-10 w-10 rounded-md object-cover", testId }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let active = true, url;
    setSrc("");
    if (fileId) api.get(`/chat/threads/${threadId}/files/${fileId}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data); if (active) setSrc(url);
    }).catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [threadId, fileId]);
  return src ? <img src={src} alt={alt} className={className} data-testid={testId} /> : <Icons.Users size={22} className="text-secondary shrink-0" />;
};

export const ChatAttachment = ({ threadId, file }) => <div className="max-w-full min-w-0">
  {file.content_type?.startsWith("audio/") && <ChatAudio threadId={threadId} file={file} />}
  {file.content_type?.startsWith("image/") && <ChatImage threadId={threadId} fileId={file.id} alt={file.filename} testId={`chat-image-${file.id}`} className="max-h-64 w-full max-w-sm object-contain rounded-md mb-2" />}
  <button data-testid={`chat-file-${file.id}`} onClick={() => downloadChatFile(`/chat/threads/${threadId}/files/${file.id}`, file.filename)} className="inline-flex max-w-full items-center gap-2 text-sm text-primary hover:underline"><Icons.Download size={15} className="shrink-0" /><span className="[overflow-wrap:anywhere]">{file.filename}</span></button>
</div>;

export const ChatAudio = ({ threadId, file }) => {
  const [src, setSrc] = useState(""), [failed, setFailed] = useState(false), [busy, setBusy] = useState(false), [status, setStatus] = useState(file.drive_status);
  useEffect(() => { setStatus(file.drive_status); }, [file.drive_status]);
  useEffect(() => {
    let active = true, url;
    api.get(`/chat/threads/${threadId}/files/${file.id}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data); if (active) setSrc(url); else URL.revokeObjectURL(url);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [threadId, file.id]);
  const retry = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/chat/threads/${threadId}/files/${file.id}/retry-drive`); setStatus(data.drive_status); }
    catch { toast.error("The Drive copy could not be retried."); } setBusy(false);
  };
  return <div className="space-y-2 my-2 max-w-full" data-testid={`chat-voice-${file.id}`}>
    {src ? <audio controls preload="metadata" src={src} data-testid={`chat-audio-${file.id}`} className="w-full max-w-sm min-w-0 h-10" /> : <p data-testid={`chat-audio-loading-${file.id}`} className="text-xs text-muted-foreground">{failed ? "Voice note unavailable." : "Loading voice note…"}</p>}
    <div className="flex flex-wrap gap-2 items-center text-xs"><span data-testid={`chat-drive-status-${file.id}`} className={status === "saved" ? "text-emerald-600" : "text-muted-foreground"}>{status === "saved" ? "Saved to Google Drive" : status === "not_connected" ? "Drive copy pending — existing Drive connection unavailable here" : status === "failed" ? "Drive copy pending — upload failed" : "Drive copy pending"}</span>
      {status !== "saved" && <button type="button" data-testid={`chat-drive-retry-${file.id}`} disabled={busy} onClick={retry} className="text-primary underline disabled:opacity-50">{busy ? "Retrying…" : "Retry Drive copy"}</button>}</div>
  </div>;
};